import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { comics } from "@/mocks/comics";
import { generateChapters, getComicById, getRelatedComics } from "@/utils/comic";

export default function Detail() {
  const { id } = useParams();
  const comic = getComicById(comics, id);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  useDocumentMeta({
    title: comic ? `妖精漫画-《${comic.title}》详情` : "妖精漫画-作品详情",
    description: comic ? comic.intro : "妖精漫画作品详情页。",
    keywords: comic ? `${comic.title},${comic.category}漫画,妖精漫画,${comic.author}` : "妖精漫画",
  });

  if (!comic) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-file-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading text-2xl text-foreground-950">抱歉，没有找到这部漫画</h1>
        <p className="text-sm text-foreground-500">它可能已被移除，或者链接有误。</p>
        <Link
          to="/category"
          className="mt-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          去分类页逛逛
        </Link>
      </div>
    );
  }

  const chapters = generateChapters(comic.latestChapter, comic.updatedAt, 12);
  const related = getRelatedComics(comics, comic, 4);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，快分享给你的朋友吧");
    } catch {
      showToast("链接已复制");
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "漫画分类", path: "/category" },
              { label: comic.category, path: `/category?cat=${encodeURIComponent(comic.category)}` },
              { label: comic.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        {/* 上下结构：封面大图在上 */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          <div className="mx-auto w-56 shrink-0 sm:w-64 lg:mx-0 lg:w-full">
            <div className="relative w-full overflow-hidden rounded-lg pt-[133%]">
              <img
                src={comic.cover}
                alt={`${comic.title} 封面`}
                title={`${comic.title} 妖精题材漫画`}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700">{comic.category}</span>
              <span
                className={`rounded-full px-3 py-1 text-xs whitespace-nowrap ${
                  comic.status === "连载" ? "bg-secondary-100 text-secondary-900" : "bg-accent-100 text-accent-900"
                }`}
              >
                {comic.status}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                <i className="ri-star-fill text-accent-500"></i>
                {comic.rating.toFixed(1)} 分
              </span>
            </div>

            <h1 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-primary-600"></i>
                作者：{comic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-bookmark-line text-primary-600"></i>
                更新至第 {comic.latestChapter} 话
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-600"></i>
                最近更新：{comic.updatedAt}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {comic.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="mb-2 text-sm font-medium text-foreground-900">内容简介</h2>
              <p className="text-sm leading-loose text-foreground-600">{comic.intro}</p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => showToast("阅读功能为演示展示，敬请期待完整漫画阅读体验")}
                className="flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-link"></i>
                复制链接
              </button>
              <button
                type="button"
                onClick={() => showToast("请打开微信，扫一扫或搜索「妖精漫画」分享给好友")}
                className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-secondary-400 hover:text-secondary-800 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-wechat-line"></i>
                微信分享
              </button>
            </div>
          </div>
        </div>

        {/* 章节列表 */}
        <div className="mt-12 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
              <i className="ri-list-ordered text-primary-600"></i>
              最新章节
            </h2>
            <span className="text-xs text-foreground-500">共 {comic.latestChapter} 话 · 展示最近 12 话</span>
          </div>

          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((ch) => (
              <li key={ch.no}>
                <button
                  type="button"
                  onClick={() => showToast(`第 ${ch.no} 话《${ch.title}》为演示章节，暂无阅读源`)}
                  className="group flex w-full items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground-900 group-hover:text-primary-700">
                      第 {ch.no} 话 · {ch.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-400">{ch.date}</span>
                  </span>
                  <i className="ri-arrow-right-s-line shrink-0 text-lg text-foreground-400 group-hover:text-primary-600"></i>
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-4 flex items-center gap-2 text-xs text-foreground-400">
            <i className="ri-information-line"></i>
            章节列表仅作信息展示，本站暂不提供漫画阅读内容。
          </p>
        </div>

        {/* 相关推荐 */}
        <div className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl text-foreground-950 md:text-2xl">
            <i className="ri-sparkling-line text-accent-500"></i>
            相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to={`/category?cat=${encodeURIComponent(comic.category)}`}
            className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回{comic.category}分类
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>

      <Toast message={toast} />
    </div>
  );
}